import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '../../components/base/SeoHead';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  description: string;
  fullDescription: string;
  features: string[];
  specifications: { [key: string]: string };
  images: string[];
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products: { [key: number]: Product } = {
    1: {
      id: 1,
      name: 'MOFU 智能寵物空氣清淨機',
      price: 9900,
      image: 'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/7d4217cd548672bb2ebe9dfb77a9d576.jpeg',
      category: '空氣清淨機',
      description: '專為寵物家庭設計，有效去除異味與過敏原',
      fullDescription: 'MOFU 智能寵物空氣清淨機採用最新的 HEPA H13 濾網技術，能有效過濾 99.97% 的空氣中微粒，包括寵物毛髮、皮屑、異味分子等。搭配香氛功能，可為您和毛孩打造最舒適且寵物友善香氛的居家環境。',
      features: [
        'HEPA H13 高效濾網，過濾 99.97% 微粒',
        '活性碳濾網',
        '貓過敏原過濾',
        '狗過敏原過濾',
        '寵物除氨過濾',
        '香氛噴霧功能',
        '超靜音運轉，睡眠模式僅 25dB',
        '適用 15 坪空間',
        '寵物友善設計，防止誤觸',
        'APP 遠端控制功能'
      ],
      specifications: {
        '適用坪數': '15 坪',
        'CADR值': '350 m³/h',
        '噪音值': '25-55 dB',
        '功率': '45W',
        '尺寸': '30 x 30 x 60 cm',
        '重量': '6.5 kg'
      },
      images: [
        'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/7d4217cd548672bb2ebe9dfb77a9d576.jpeg',
        'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/366a75c184363e7c2843b92347e30700.jpeg',
        'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/bb71a91a25c2cecbcce290fbd7d5481c.jpeg'
      ]
    },
    2: {
      id: 2,
      name: '寵物友善薰衣草擴香組',
      price: 1280,
      image: 'https://public.readdy.ai/ai/img_res/d7f9e197152068c91634af89de68752e.jpg',
      category: '香氛產品',
      description: '天然薰衣草精油，舒緩放鬆',
      fullDescription: '採用法國普羅旺斯薰衣草精油，經過專業調配，確保對寵物完全安全。溫和的薰衣草香氣能幫助舒緩情緒、改善睡眠品質，同時有效淨化空氣。搭配天然藤枝擴香棒，持續散發迷人香氣長達 3 個月。',
      features: [
        '100% 天然薰衣草精油',
        '寵物安全認證配方',
        '持續擴香 3 個月',
        '附贈 8 支天然藤枝',
        '精美玻璃瓶身設計',
        '無酒精、無化學添加'
      ],
      specifications: {
        '容量': '150ml',
        '香調': '薰衣草',
        '擴香時間': '約 3 個月',
        '藤枝數量': '8 支',
        '瓶身材質': '玻璃',
        '產地': '台灣'
      },
      images: [
        'https://readdy.ai/api/search-image?query=elegant%20lavender%20reed%20diffuser%20set%20with%20natural%20wooden%20sticks%20in%20glass%20bottle%20on%20clean%20white%20background%2C%20pet-safe%20aromatherapy%20product%2C%20soft%20lighting%2C%20minimalist%20style%2C%20professional%20product%20photography&width=600&height=600&seq=detail2a&orientation=squarish',
        'https://readdy.ai/api/search-image?query=reed%20diffuser%20close%20up%20showing%20lavender%20oil%20and%20natural%20sticks%20on%20clean%20white%20background%2C%20detailed%20product%20shot%2C%20professional%20lighting&width=600&height=600&seq=detail2b&orientation=squarish',
        'https://readdy.ai/api/search-image?query=lavender%20diffuser%20in%20cozy%20bedroom%20with%20pet%2C%20lifestyle%20photography%2C%20warm%20natural%20lighting%2C%20peaceful%20atmosphere&width=600&height=600&seq=detail2c&orientation=squarish'
      ]
    }
  };

  const product = products[parseInt(id || '1')] || products[1];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首頁", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "線上商城", "item": `${siteUrl}/shop` },
          { "@type": "ListItem", "position": 3, "name": product.name, "item": `${siteUrl}/product/${product.id}` }
        ]
      },
      {
        "@type": "Product",
        "@id": `${siteUrl}/product/${product.id}#product`,
        "name": product.name,
        "description": product.fullDescription,
        "image": product.images,
        "sku": `MOFU-${product.id}`,
        "brand": {
          "@type": "Brand",
          "name": "MOFU 淨寵居"
        },
        "category": product.category,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "TWD",
          "availability": "https://schema.org/InStock",
          "url": `${siteUrl}/product/${product.id}`,
          "seller": {
            "@type": "Organization",
            "name": "MOFU 淨寵居",
            "url": siteUrl
          },
          "priceValidUntil": "2025-12-31",
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": "0",
              "currency": "TWD"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 3,
                "maxValue": 5,
                "unitCode": "DAY"
              }
            }
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "127",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    ]
  };

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({ ...product, quantity });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));

    const message = document.createElement('div');
    message.className = 'fixed top-24 right-6 bg-coral-500 text-white px-6 py-3 rounded-full shadow-lg z-50';
    message.textContent = `已加入 ${quantity} 件商品到購物車`;
    document.body.appendChild(message);
    setTimeout(() => message.remove(), 2000);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <SeoHead
        title={`${product.name} - ${product.category} | MOFU 淨寵居`}
        description={`${product.fullDescription.substring(0, 130)}。NT$ ${product.price.toLocaleString()}，全台免運，7天鑑賞期。MOFU 淨寵居寵物友善香氛產品。`}
        keywords={`${product.name},${product.category},寵物空氣清淨機,寵物友善香氛,MOFU淨寵居`}
        ogImage={product.image}
        schema={schema}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-coral-500 cursor-pointer whitespace-nowrap">首頁</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/shop" className="hover:text-coral-500 cursor-pointer whitespace-nowrap">線上商城</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Images */}
          <div>
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-gray-50 mb-4">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-gray-50 ${
                    selectedImage === index ? 'ring-4 ring-coral-500' : ''
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <span className="inline-block px-3 py-1 bg-coral-100 text-coral-500 rounded-full text-sm font-medium mb-4 whitespace-nowrap">
              {product.category}
            </span>
            <h1 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product.name}
            </h1>
            <p className="text-gray-600 text-lg mb-6">{product.description}</p>
            
            <div className="flex items-baseline gap-4 mb-8">
              <span className="text-4xl font-bold text-coral-500">
                NT$ {product.price.toLocaleString()}
              </span>
            </div>

            <div className="border-t border-b border-gray-200 py-6 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-gray-700 font-medium">數量：</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={addToCart}
                  className="flex-1 px-8 py-4 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  加入購物車
                </button>
                <button className="px-6 py-4 border-2 border-coral-500 text-coral-500 rounded-full hover:bg-coral-50 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3 text-gray-600">
                <i className="ri-truck-line text-coral-500 text-xl"></i>
                <span>全台免運，3-5 個工作天到貨</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <i className="ri-shield-check-line text-coral-500 text-xl"></i>
                <span>7 天鑑賞期，安心購物</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <i className="ri-customer-service-line text-coral-500 text-xl"></i>
                <span>專業客服團隊，隨時為您服務</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="bg-gray-50 rounded-3xl p-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              產品介紹
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">{product.fullDescription}</p>

            <h3 className="text-2xl font-bold text-gray-800 mb-4">產品特色</h3>
            <ul className="space-y-3 mb-8">
              {product.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-coral-500 text-xl mt-1"></i>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-2xl font-bold text-gray-800 mb-4">產品規格</h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(product.specifications).map(([key, value]) => (
                <div key={key} className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-600">{key}</span>
                  <span className="text-gray-800 font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}